define([
	"dojo/_base/declare",
	"csi",
	"csi/_base/underscore",
	"../SizeChart",
	"../TwoDimSizesContainerMixin"
], function(declare,csi,_,SizeChart,TwoDimSizesContainerMixin) {

	/**
	 * @typedef {Object} AbsoluteAndAbsoluteIgnoreBlank
	 * Each object represents the double vectors in both absolute mode and absolute ignore blank
	 * The format of the object is following:
	 * @property {Double[]} abs - double vector in absolute mode
	 * @property {Double[]} absIgnoreBlank  - double vector in in absolute-ignoring-blank mode
	 */

	return  declare(TwoDimSizesContainerMixin, {
		// Object: SizeChartRevision or FinalInspectionSizeChart to support 2-d sizes.

		subSizeRanges: null,

		// Fields only valid for 2-d sizes. Requirements: (1)SizeChart with 2-d sizes, (2)has simple size range
		baseSize: null,

		constructor: function(/*Cache*/cache, /*Node*/sizeChartRev) {
			this.node = sizeChartRev;
			this.subSizeRanges = cache.getNodes(sizeChartRev.SizeChartSubSizeRanges);
			this.sizes = cache.getNodes(sizeChartRev.Sizes);

			// For 2-d support
			this._isTwoDim = !_.isEmpty(sizeChartRev.SizeChartDimension2Sizes);
			if (!this._isTwoDim)
				return;

			this.baseSize = cache.getNode(this.subSizeRanges[0].BaseSize);
			this.baseIndex = this.subSizeRanges[0].BaseSizeIndex;  // We support 2-d sizeChart only on simple size range
			this.baseSizeDim1Index = _.indexOf(this.node.SizeChartDimension1Sizes, this.baseSize.Dimension1Size);
			this.baseSizeDim2Index = _.indexOf(this.node.SizeChartDimension2Sizes, this.baseSize.Dimension2Size);

			this.dim1Sizes = this.node.SizeChartDimension1Sizes;
			this.dim2Sizes = this.node.SizeChartDimension2Sizes;

			// add {Dime1Size, Dim2Size} indices to map
			var obj = this._mapIncrementIndexToDimSizesIndices = {};
			var dim1Map = csi.hashArray(sizeChartRev.SizeChartDimension1Sizes, null, Number);
			var dim2Map = csi.hashArray(sizeChartRev.SizeChartDimension2Sizes, null, Number);

			var iSize = 0;
			for (var iSize = 0; iSize < this.sizes.length; ++iSize) {
				var curSize = this.sizes[iSize];
				obj[iSize] = {
						dim1SizeIndex: dim1Map[curSize.Dimension1Size],
						dim2SizeIndex: dim2Map[curSize.Dimension2Size]
				};
			}

			// map {dim1SizeIndex, {dim2SizeIndex, vectorIndex})
			var obj2 = {};
			var iDim1 = 0;
			var dim2ToVector;
			for (iDim1 = 0; iDim1 < this.getDim1SizeCount(); ++iDim1) {
				dim2ToVector = {};
				for (iSize = 0; iSize < this.sizes.length; ++iSize) {
					var curDim1Dim2 = this._mapIncrementIndexToDimSizesIndices[iSize];
					if (curDim1Dim2.dim1SizeIndex == iDim1) {
						dim2ToVector[curDim1Dim2.dim2SizeIndex] = iSize;
					}
				}
				obj2[iDim1] = dim2ToVector;
			}
			this._mapDim1ToDim2Indices = obj2;
		},

		absoluteToIncrement: function(/*Double[]*/abs, /*Boolean*/displayTwoDim) {
			// summary: convert the double vector from increment mode to absolute mode
			var	inc = [].concat(abs);
			var absIgnoreBlank = [].concat(abs);

			var baseFunc = function(x) {
				absIgnoreBlank[x] = SizeChart.isNotInitialized(abs[x]) ? 0.0 : abs[x];
				abs[x] = inc[x];
			};

			if (displayTwoDim) {
				this.iterate2D(
					baseFunc,
					function(x, y) {
						var index = this.getIndex(x, y);
						if (index == undefined || index < 0) // 2D SizeRange with hole(s)
							return;

						if (!SizeChart.isNotInitialized(abs[index])) {
							absIgnoreBlank[index] = abs[index];
							inc[index] = SizeChart.sumDoubleValues(absIgnoreBlank[index], -absIgnoreBlank[this.getCompareIndex(index)]);
						} else {
							absIgnoreBlank[index] = absIgnoreBlank[this.getCompareIndex(index)];
						}
					}
				);
			} else {
				this.iterate1D(
					baseFunc,
					function(x) {
						if (!SizeChart.isNotInitialized(abs[x])) {
							absIgnoreBlank[x] = abs[x];
							inc[x] = SizeChart.sumDoubleValues(absIgnoreBlank[x], -absIgnoreBlank[x+1]);
						} else {
							absIgnoreBlank[x] = absIgnoreBlank[x+1];
						}
					},
					function(x) {
						if (!SizeChart.isNotInitialized(abs[x])) {
							absIgnoreBlank[x] = abs[x];
							inc[x] = SizeChart.sumDoubleValues(absIgnoreBlank[x], -absIgnoreBlank[x-1]);
						} else {
							absIgnoreBlank[x] = absIgnoreBlank[x-1];
						}
					}
				);
			}

			return inc;
		},

		/**
		 * Convert the double vector from increment mode to object containing vectors in absolute mode and absolute ignore blank (+/-999)
		 * @param {Double[]} inc
		 * @param {Boolean} displayTwoDim
		 * @return {AbsoluteAndAbsoluteIgnoreBlank}
		 */
		incrementToAbsoluteAndIgnoreBlank: function(inc, displayTwoDim) {
			var	abs = [].concat(inc);
			var absIgnoreBlank = [].concat(inc);

			var baseFunc = function(x) {
				absIgnoreBlank[x] = SizeChart.isNotInitialized(inc[x]) ? 0.0 : inc[x];
				abs[x] = inc[x];
			};

			if (displayTwoDim) {
				this.iterate2D(
					baseFunc,
					function(x, y) {
						var index = this.getIndex(x, y);
						if (index == undefined || index < 0) // 2D SizeRange with hole(s)
							return;

						absIgnoreBlank[index] = SizeChart.sumDoubleValues(absIgnoreBlank[this.getCompareIndex(index)], inc[index]);
						if (!SizeChart.isNotInitialized(inc[index]))
							abs[index] = absIgnoreBlank[index];
					}
				);
			} else {
				this.iterate1D(
					baseFunc,
					function(x) {
						absIgnoreBlank[x] = SizeChart.sumDoubleValues(absIgnoreBlank[x+1], inc[x]);
						if (!SizeChart.isNotInitialized(inc[x]))
							abs[x] = absIgnoreBlank[x];
					},
					function(x) {
						absIgnoreBlank[x] = SizeChart.sumDoubleValues(absIgnoreBlank[x-1], inc[x]);
						if (!SizeChart.isNotInitialized(inc[x]))
							abs[x] = absIgnoreBlank[x];
					}
				);
			}

			return {abs: abs, absIgnoreBlank: absIgnoreBlank};
		},

		incrementToRelative: function(/*Double[]*/inc, /*Boolean*/displayTwoDim) {
			// summary: convert the double vector from increment mode to relative mode
			var	relative = [].concat(inc);
			var relativeIgnoreBlank = [].concat(inc);

			var baseFunc = function(x) {
				relativeIgnoreBlank[x] = 0.0;
				relative[x] = 0.0;
			};

			if (displayTwoDim) {
				this.iterate2D(
					baseFunc,
					function(x, y) {
						var index = this.getIndex(x, y);
						if (index == undefined || index < 0) // 2D SizeRange with hole(s)
							return;

						relativeIgnoreBlank[index] = SizeChart.sumDoubleValues(relativeIgnoreBlank[this.getCompareIndex(index)], inc[index]);
						if (!SizeChart.isNotInitialized(inc[index]))
							relative[index] = relativeIgnoreBlank[index];
					}
				);
			} else {
				this.iterate1D(
					baseFunc,
					function(x) {
						relativeIgnoreBlank[x] = SizeChart.sumDoubleValues(relativeIgnoreBlank[x+1], inc[x]);
						if (!SizeChart.isNotInitialized(inc[x]))
							relative[x] = relativeIgnoreBlank[x];
					},
					function(x) {
						relativeIgnoreBlank[x] = SizeChart.sumDoubleValues(relativeIgnoreBlank[x-1], inc[x]);
						if (!SizeChart.isNotInitialized(inc[x]))
							relative[x] = relativeIgnoreBlank[x];
					}
				);
			}

			return relative;
		}
	});

});
